<!-- Bass-Seite als Knopfraster (waagrecht oder `vertical` hochkant, dann mit Schalter „Darstellung umdrehen“): je Knopf der Griffschrift-Name, optional der Klang einer Balgrichtung, hervorgehobene Knöpfe über `activeNames`. Mit `playable` (Default) klingt ein angeklickter Knopf; mit `editable` wählbar (v-model:selected) und erweiterbar (emits add-button). -->
<script setup lang="ts">
import {useLocalStorage} from '@vueuse/core'
import {computed} from 'vue'

import {useBassButtonTones} from '@/composables/useBassButtonTones'
import type {BassButton, BassLayoutSettings} from '@/generated'

import BoardFlipToggle from './BoardFlipToggle.vue'
import {playButtonTones} from './buttonTone'
import {BOARDS_FLIPPED_STORAGE_KEY} from './instrumentData'

export type BassButtonPosition = { row: number; button: number }

const BASS_TONE_MS = 700

const {
  layout,
  direction = 'druck',
  showSounds = true,
  activeNames = new Set<string>(),
  editable = false,
  playable = true,
  vertical = false,
} = defineProps<{
  layout: BassLayoutSettings
  /** Hochkant wie `InstrumentBody vertical`: Reihen als Spalten, erste Reihe links. */
  vertical?: boolean
  /** Balgrichtung, deren Klang unter dem Namen steht und beim Anklicken ertönt. */
  direction?: 'druck' | 'zug'
  /** Klang der Balgrichtung unter dem Namen anzeigen; aus = nur der Griffschrift-Name. */
  showSounds?: boolean
  /** Griffschrift-Namen der hervorgehobenen Knöpfe. */
  activeNames?: Set<string>
  /** Knöpfe sind wählbar und je Reihe lässt sich einer anhängen. */
  editable?: boolean
  /** Ein angeklickter Knopf klingt (Töne vom Backend, `useBassButtonTones`). */
  playable?: boolean
}>()

const selected = defineModel<BassButtonPosition | null>('selected', {default: null})

const emit = defineEmits<{
  'add-button': [rowIndex: number]
}>()

const {tones} = useBassButtonTones(() => (playable ? layout : null))

const clickable = computed(() => editable || playable)

const boardsFlipped = useLocalStorage(BOARDS_FLIPPED_STORAGE_KEY, false)
// Knopf-Index 0 sitzt im Bass am Instrument UNTEN (im Diskant oben). Hochkant steht deshalb
// ungedreht die Knopfreihenfolge umgekehrt – so zeigen Diskant und Bass dieselbe Lage.
const reversed = computed(() => vertical && !boardsFlipped.value)

function press(position: BassButtonPosition) {
  if (editable) selected.value = position
  // Ohne `playable` ist die Abfrage aus und `tones` leer.
  playButtonTones(tones.value?.[direction][position.row]?.[position.button] ?? [], BASS_TONE_MS)
}

/**
 * Reihen in Anzeige-Reihenfolge (waagrecht letzte Reihe oben, hochkant erste Reihe links – wie
 * `InstrumentBody`) samt Original-Index im Layout.
 * Nicht-mutierend: ein `.reverse()` direkt auf dem reaktiven Store-Array im Template
 * triggerte den eigenen Render-Effekt endlos – im Production-Build ohne Vues dev-only
 * Rekursions-Guard fror die Seite ein. `index` bleibt der Layout-Index, damit `selected`
 * und `add-button` die richtige Reihe treffen. Die Knöpfe je Reihe ebenso, bei `reversed`
 * umgekehrt – in der DOM-Reihenfolge, damit die Tab-Reihenfolge dem Bild folgt.
 */
const displayRows = computed(() => {
  const rows = (layout.rows ?? []).map((row, index) => {
    const buttons = (row.buttons ?? []).map((button, buttonIndex) => ({button, buttonIndex}))
    return {row, index, buttons: reversed.value ? buttons.reverse() : buttons}
  })
  return vertical ? rows : rows.reverse()
})

/** Reihenversatz: waagrecht nach rechts, hochkant nach unten. */
function rowIndent(rowIndex: number): Record<string, string> {
  return {[vertical ? 'paddingTop' : 'paddingLeft']: `${(rowIndex + 1) * 4 * 7.5}px`}
}

/** Was der Knopf in der gewählten Balgrichtung erzeugt – leer bleibt sichtbar leer. */
function sounds(button: BassButton): string {
  return (direction === 'druck' ? button.druck : button.zug) ?? '–'
}

function isSelected(rowIndex: number, buttonIndex: number): boolean {
  return selected.value?.row === rowIndex && selected.value?.button === buttonIndex
}

function buttonClass(button: BassButton, rowIndex: number, buttonIndex: number): string {
  if (activeNames.has(button.griffschrift)) return 'border-sky-700 bg-sky-600 text-white'
  const hover = clickable.value ? ' hover:bg-teal-100' : ''
  if (isSelected(rowIndex, buttonIndex)) return `border-stone-300 bg-sky-50 ring-2 ring-sky-500${hover}`
  return `border-stone-300 bg-white${hover}`
}
</script>

<template>
  <section
      class="relative rounded-xl border-4 border-gray-500/80 bg-gradient-to-tr from-sky-100/40 to-cyan-200/30 shadow-lg"
      :class="vertical ? 'flex w-fit items-start gap-2 px-3 py-5' : 'px-5 py-3'"
      aria-label="Bass-Seite"
  >
    <!-- Lage am Instrument (Regel an `reversed`); waagrecht quer gestellt wie das Instrument. -->
    <span
        class="pointer-events-none absolute text-[10px] font-bold uppercase leading-none tracking-wide text-primary/60"
        :class="vertical ? 'top-0.5 left-1/2 -translate-x-1/2' : 'left-1 top-1/2 -translate-y-1/2 [writing-mode:vertical-rl]'"
    >
      {{ reversed ? 'oben' : 'unten' }}
    </span>
    <span
        class="pointer-events-none absolute text-[10px] font-bold uppercase leading-none tracking-wide text-primary/60"
        :class="vertical ? 'bottom-0.5 left-1/2 -translate-x-1/2' : 'right-1 top-1/2 -translate-y-1/2 [writing-mode:vertical-rl]'"
    >
      {{ reversed ? 'unten' : 'oben' }}
    </span>
    <BoardFlipToggle v-if="vertical" v-model:flipped="boardsFlipped"/>
    <div v-if="!layout.rows?.length" class="py-6 text-center text-sm text-primary">
      Keine Reihen konfiguriert.
    </div>
    <div
        v-for="{row, index: rowIndex, buttons} in displayRows" :key="rowIndex"
        class="flex items-center gap-2"
        :class="vertical ? 'flex-col' : 'mb-2 last:mb-0'"
        :style="rowIndent(rowIndex)"
    >
      <span class=" relative left-0 shrink-0 text-xs font-bold text-primary/80">{{ row.name }}</span>
      <component
          :is="clickable ? 'button' : 'span'"
          v-for="{button, buttonIndex} in buttons"
          :key="buttonIndex"
          :type="clickable ? 'button' : undefined"
          class="flex h-12 w-12 flex-col items-center justify-center rounded-full border-2 text-center leading-none shadow transition-colors"
          :class="buttonClass(button, rowIndex, buttonIndex)"
          @click="clickable && press({row: rowIndex, button: buttonIndex})"
      >
        <span class="text-sm font-bold">{{ button.griffschrift }}</span>
        <span v-if="showSounds" class="text-[9px] opacity-70">{{ sounds(button) }}</span>
      </component>
      <button
          v-if="editable"
          type="button"
          class="h-12 w-12 rounded-full border-2 border-dashed border-white/50 text-white/70 transition-colors hover:bg-white/10"
          :aria-label="`Knopf zu ${row.name} hinzufügen`"
          @click="emit('add-button', rowIndex)"
      >
        +
      </button>
    </div>
  </section>
</template>
